import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User as FirebaseUser } from '@firebase/auth';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AuthService } from 'application/src/app/services/auth.service';
import {
  PeechUserProfile,
  PeechUserService,
} from 'application/src/app/services/peech-user.service';
import { filter, tap } from 'rxjs';

const DEFAULT_NAME = 'Your Name';
const LOGO_AVATAR = 'assets/img/logo-icon.png';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [MatMenuModule, RouterLink, MatButtonModule],
})
export class HeaderComponent {
  name = DEFAULT_NAME;
  avatarUrl = LOGO_AVATAR;
  isLoggedIn = false;
  isLogoAvatar: boolean = false; //we hide our logo because it is the same to default avatar which makes it look ugly
  isAccountPage = false;

  private firebaseUser: FirebaseUser | null = null;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private authService: AuthService,
    private peechUser: PeechUserService,
    private router: Router
  ) {
    // Identity is recomputed on EVERY emission (including nulls) rather than filtered:
    // after a sign-out/sign-in the previous account's name/avatar must not survive into
    // the next account's session. The Firestore users/{uuid} doc is the source of truth
    // (it's what the mobile apps show); Firebase auth data is the fallback while it loads.
    this.authService.user$
      .pipe(
        tap(user => {
          this.firebaseUser = user;
          this.isLoggedIn = !!user;
          this.applyIdentity(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.peechUser.profile$
      .pipe(
        tap(profile => this.applyIdentity(profile)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        tap(event => {
          this.isAccountPage = event.url === '/account';
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // A failed avatar download falls back to the logo placeholder, like iOS. The guard
  // stops a retry loop if the placeholder itself ever failed to load.
  onAvatarError(): void {
    if (this.avatarUrl !== LOGO_AVATAR) {
      this.avatarUrl = LOGO_AVATAR;
      this.isLogoAvatar = this.isLoggedIn;
    }
  }

  private applyIdentity(profile: PeechUserProfile | null): void {
    const avatarUrl = profile?.avatarUrl || this.firebaseUser?.photoURL || null;

    this.name =
      profile?.name || this.firebaseUser?.displayName || this.firebaseUser?.email || DEFAULT_NAME;
    this.avatarUrl = avatarUrl ?? LOGO_AVATAR;
    // Only a signed-in user can show the fallback logo as their avatar; signed-out visitors
    // have no avatar at all, and hiding the brand logo for them would strip the header bare.
    this.isLogoAvatar = this.isLoggedIn && !avatarUrl;
  }
}
